import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Progress } from '../ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import {
  Search,
  Upload,
  Download,
  Loader2,
  CheckCircle,
  XCircle,
  Mail,
  Globe,
  FileSpreadsheet,
  Play,
  Pause,
  Trash2,
  Edit3,
  Plus,
  Users,
  Settings,
  RefreshCw,
  Send,
  Clock,
  AlertCircle,
  Eye,
  Languages,
  Rocket,
  X,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SEOProspectorTab = () => {
  const { language } = useLanguage();
  
  // Scraper state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [pollingInterval, setPollingInterval] = useState(null);
  const fileInputRef = useRef(null);
  
  // Campaigns state
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [sendingBatch, setSendingBatch] = useState({});
  
  // Create campaign state
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [campaignFile, setCampaignFile] = useState(null);
  const [campaignName, setCampaignName] = useState('');
  const [dailyLimit, setDailyLimit] = useState(500);
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const campaignFileRef = useRef(null);
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // Replies state
  const [selectedCampaignForReplies, setSelectedCampaignForReplies] = useState(null);
  const [campaignReplies, setCampaignReplies] = useState([]);
  const [repliesLoading, setRepliesLoading] = useState(false);
  const [repliesStats, setRepliesStats] = useState(null);

  // Workers state
  const [workers, setWorkers] = useState([]);
  const [workersSummary, setWorkersSummary] = useState([]);
  const [newWorkerName, setNewWorkerName] = useState('');
  const [showWorkerManager, setShowWorkerManager] = useState(false);

  const t = language === 'es' ? {
    title: 'SEO Prospector',
    description: 'Busca emails de contacto y envía campañas de prospección',
    scraperTab: 'Buscar Emails',
    campaignsTab: 'Campañas',
    templatesTab: 'Plantillas',
    uploadExcel: 'Subir Excel con Dominios',
    uploadDescription: 'Primera columna debe contener los dominios a buscar',
    selectFile: 'Seleccionar archivo',
    startScraping: 'Iniciar Búsqueda',
    processing: 'Procesando...',
    jobStatus: 'Estado del Job',
    totalDomains: 'Dominios totales',
    processed: 'Procesados',
    emailsFound: 'Emails encontrados',
    downloadResults: 'Descargar Resultados',
    recentJobs: 'Jobs Recientes',
    noJobs: 'No hay jobs recientes',
    pending: 'Pendiente',
    running: 'Procesando',
    completed: 'Completado',
    error: 'Error',
    campaigns: 'Campañas de Outreach',
    noCampaigns: 'No hay campañas',
    createCampaign: 'Crear Campaña',
    campaignName: 'Nombre de la campaña',
    uploadEmailsFile: 'Subir Excel con emails',
    dailyLimit: 'Límite diario de envíos',
    create: 'Crear',
    cancel: 'Cancelar',
    totalEmails: 'Emails totales',
    sent: 'Enviados',
    sendBatch: 'Enviar Lote',
    pause: 'Pausar',
    resume: 'Reanudar',
    delete: 'Eliminar',
    templates: 'Plantillas por idioma',
    defaultTemplates: 'Plantillas por defecto disponibles para:',
    subject: 'Asunto',
    body: 'Cuerpo',
    refresh: 'Actualizar',
    viewCampaign: 'Ver Campaña',
    recipients: 'Destinatarios',
    lastSent: 'Último envío',
    pendingEmails: 'Pendientes',
    failedEmails: 'Fallidos',
    domain: 'Dominio',
    email: 'Email',
    status: 'Estado',
    createdAt: 'Creado',
    // Replies tab
    repliesTab: 'Respuestas',
    campaignReplies: 'Respuestas a Campañas',
    noReplies: 'No hay respuestas aún',
    replyRate: 'Tasa de respuesta',
    uniqueRepliers: 'Contactos únicos',
    totalSent: 'Emails enviados',
    viewReply: 'Ver respuesta',
    fromCampaign: 'De campaña',
    selectCampaignToViewReplies: 'Selecciona una campaña para ver sus respuestas',
  } : {
    title: 'SEO Prospector',
    description: 'Find contact emails and send prospecting campaigns',
    scraperTab: 'Find Emails',
    campaignsTab: 'Campaigns',
    templatesTab: 'Templates',
    uploadExcel: 'Upload Excel with Domains',
    uploadDescription: 'First column must contain the domains to search',
    selectFile: 'Select file',
    startScraping: 'Start Search',
    processing: 'Processing...',
    jobStatus: 'Job Status',
    totalDomains: 'Total domains',
    processed: 'Processed',
    emailsFound: 'Emails found',
    downloadResults: 'Download Results',
    recentJobs: 'Recent Jobs',
    noJobs: 'No recent jobs',
    pending: 'Pending',
    running: 'Processing',
    completed: 'Completed',
    error: 'Error',
    campaigns: 'Outreach Campaigns',
    noCampaigns: 'No campaigns',
    createCampaign: 'Create Campaign',
    campaignName: 'Campaign name',
    uploadEmailsFile: 'Upload Excel with emails',
    dailyLimit: 'Daily send limit',
    create: 'Create',
    cancel: 'Cancel',
    totalEmails: 'Total emails',
    sent: 'Sent',
    sendBatch: 'Send Batch',
    pause: 'Pause',
    resume: 'Resume',
    delete: 'Delete',
    templates: 'Templates by language',
    defaultTemplates: 'Default templates available for:',
    subject: 'Subject',
    body: 'Body',
    refresh: 'Refresh',
    viewCampaign: 'View Campaign',
    recipients: 'Recipients',
    lastSent: 'Last sent',
    pendingEmails: 'Pending',
    failedEmails: 'Failed',
    domain: 'Domain',
    email: 'Email',
    status: 'Status',
    createdAt: 'Created',
    // Replies tab
    repliesTab: 'Replies',
    campaignReplies: 'Campaign Replies',
    noReplies: 'No replies yet',
    replyRate: 'Reply rate',
    uniqueRepliers: 'Unique contacts',
    totalSent: 'Emails sent',
    viewReply: 'View reply',
    fromCampaign: 'From campaign',
    selectCampaignToViewReplies: 'Select a campaign to view its replies',
  };

  // Fetch data on mount
  useEffect(() => {
    fetchJobs();
    fetchCampaigns();
    fetchTemplates();
    fetchWorkers();
    fetchWorkersSummary();
  }, []);

  // Auto-detect and resume polling for in-progress jobs when component mounts
  useEffect(() => {
    const checkForActiveJobs = async () => {
      try {
        const response = await axios.get(`${API}/seo-prospector/jobs`);
        const jobsList = response.data.jobs || [];
        
        // Find any job that is still processing
        const activeJob = jobsList.find(job => 
          job.status === 'pending' || job.status === 'processing'
        );
        
        if (activeJob) {
          setCurrentJob(activeJob);
          startPolling(activeJob.id);
          console.log(`Resuming monitoring for job ${activeJob.id}`);
        }
      } catch (error) {
        console.error('Error checking active jobs:', error);
      }
    };
    
    checkForActiveJobs();
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // ========================
  // SCRAPER FUNCTIONS
  // ========================

  // Export full SEO Prospector backup
  const exportSeoBackup = async () => {
    try {
      toast.info(language === 'es' ? 'Generando copia completa...' : 'Generating full backup...');
      const response = await axios.get(`${API}/seo-prospector/backup/export`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '').slice(0, 15);
      a.href = url;
      a.download = `seo_prospector_backup_${stamp}.json`;
      a.click();
      URL.revokeObjectURL(url);
      const counts = response.data.counts || {};
      const total = Object.values(counts).reduce((a, b) => a + b, 0);
      toast.success(language === 'es' ? `Copia completa descargada (${total} elementos)` : `Full backup downloaded (${total} items)`);
    } catch (error) {
      toast.error(language === 'es' ? 'Error generando copia' : 'Error generating backup');
    }
  };

  // Import full SEO Prospector backup
  const importSeoBackup = async (file) => {
    if (!file) return;
    const confirmMsg = language === 'es'
      ? '¿Importar copia completa de SEO Prospector? Se añadirá sin borrar nada existente.'
      : 'Import full SEO Prospector backup? Will be added without deleting anything existing.';
    if (!window.confirm(confirmMsg)) return;
    try {
      toast.info(language === 'es' ? 'Importando... esto puede tardar' : 'Importing... this may take a while');
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/seo-prospector/backup/import`, formData);
      const summary = response.data.summary || {};
      const totalCreated = Object.values(summary).reduce((a, b) => a + (b.created || 0), 0);
      const totalSkipped = Object.values(summary).reduce((a, b) => a + (b.skipped || 0), 0);
      toast.success(language === 'es'
        ? `Restauración OK: ${totalCreated} nuevos, ${totalSkipped} ya existían`
        : `Restore OK: ${totalCreated} new, ${totalSkipped} already existed`);
      if ((response.data.errors || []).length > 0) {
        toast.warning(`${response.data.errors.length} errores (revisa consola)`);
        console.warn('Import errors:', response.data.errors);
      }
      fetchJobs();
      fetchCampaigns();
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error importando' : 'Error importing'));
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/seo-prospector/jobs`);
      setJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setUploadFile(file);
    } else {
      toast.error(language === 'es' ? 'Solo archivos Excel (.xlsx, .xls)' : 'Only Excel files (.xlsx, .xls)');
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error(language === 'es' ? 'Selecciona un archivo' : 'Select a file');
      return;
    }

    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      const response = await axios.post(`${API}/seo-prospector/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });

      toast.success(language === 'es' ? 'Job iniciado' : 'Job started');
      
      // Start polling for job status
      const jobId = response.data.job_id;
      setCurrentJob({ id: jobId, status: 'pending', ...response.data });
      startPolling(jobId);
      
      setUploadFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error subiendo archivo' : 'Error uploading file'));
    } finally {
      setUploading(false);
    }
  };

  const startPolling = (jobId) => {
    // Clear any existing polling
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/seo-prospector/job/${jobId}`);
        setCurrentJob(response.data);

        if (response.data.status === 'completed' || response.data.status === 'error') {
          clearInterval(interval);
          setPollingInterval(null);
          fetchJobs();
          
          if (response.data.status === 'completed') {
            toast.success(
              language === 'es' 
                ? `¡Completado! ${response.data.emails_found} emails encontrados` 
                : `Completed! ${response.data.emails_found} emails found`
            );
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 2000); // Poll every 2 seconds

    setPollingInterval(interval);
  };

  const handleDownloadResult = async (jobId) => {
    try {
      const response = await axios.get(`${API}/seo-prospector/job/${jobId}/download`, {
        responseType: 'blob',
        timeout: 60000
      });

      if (response.data.size === 0) {
        toast.error(language === 'es' ? 'El archivo está vacío' : 'File is empty');
        return;
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `emails_prospector_${jobId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(language === 'es' ? 'Descarga iniciada' : 'Download started');
    } catch (error) {
      const msg = error.response?.status === 404 
        ? (language === 'es' ? 'Job no encontrado' : 'Job not found')
        : error.code === 'ECONNABORTED'
        ? (language === 'es' ? 'Timeout - archivo muy grande. Intenta de nuevo' : 'Timeout - large file. Try again')
        : (language === 'es' ? 'Error descargando' : 'Error downloading');
      toast.error(msg);
    }
  };

  // Download partial results (during processing)
  const handleDownloadPartial = async (jobId) => {
    try {
      const response = await axios.get(`${API}/seo-prospector/job/${jobId}/download-partial`, {
        responseType: 'blob',
        timeout: 60000
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `emails_prospector_parcial_${jobId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(language === 'es' ? 'Descarga parcial iniciada' : 'Partial download started');
    } catch (error) {
      toast.error(language === 'es' ? 'No hay resultados disponibles aún' : 'No results available yet');
    }
  };

  // Download only found emails
  const handleDownloadFound = async (jobId) => {
    try {
      const response = await axios.get(`${API}/seo-prospector/job/${jobId}/download-found`, {
        responseType: 'blob',
        timeout: 60000
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `emails_encontrados_${jobId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(language === 'es' ? 'Descarga de encontrados iniciada' : 'Found emails download started');
    } catch (error) {
      toast.error(language === 'es' ? 'No hay emails encontrados' : 'No emails found');
    }
  };

  // Download not found (for retry)
  const handleDownloadNotFound = async (jobId) => {
    try {
      const response = await axios.get(`${API}/seo-prospector/job/${jobId}/download-not-found`, {
        responseType: 'blob',
        timeout: 60000
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `sin_email_${jobId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(language === 'es' ? 'Descarga de no encontrados iniciada' : 'Not found download started');
    } catch (error) {
      toast.error(language === 'es' ? 'Todos los dominios tienen email' : 'All domains have email');
    }
  };

  // Resume an interrupted job
  const handleResumeJob = async (jobId) => {
    try {
      const response = await axios.post(`${API}/seo-prospector/job/${jobId}/resume`);
      toast.success(language === 'es' 
        ? `Reanudando desde el dominio ${response.data.resuming_from + 1}` 
        : `Resuming from domain ${response.data.resuming_from + 1}`
      );
      
      // Start polling for updates
      startPolling(jobId);
      fetchJobs();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error';
      toast.error(msg);
    }
  };

  // Select a job from the list to view its status
  const selectJob = async (job) => {
    // Set as current job
    setCurrentJob(job);
    
    // If the job is still processing, start polling
    if (job.status === 'pending' || job.status === 'processing') {
      startPolling(job.id);
      toast.info(language === 'es' ? 'Monitoreando job en progreso...' : 'Monitoring job in progress...');
    }
  };

  // Delete a single job
  const deleteJob = async (jobId) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar este job?' : 'Delete this job?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/seo-prospector/job/${jobId}`);
      toast.success(language === 'es' ? 'Job eliminado' : 'Job deleted');
      
      // Clear current job if it was the deleted one
      if (currentJob?.id === jobId) {
        setCurrentJob(null);
      }
      
      fetchJobs();
    } catch (error) {
      toast.error(language === 'es' ? 'Error eliminando job' : 'Error deleting job');
    }
  };

  // Delete all completed jobs
  const deleteCompletedJobs = async () => {
    const completedCount = jobs.filter(j => j.status === 'completed').length;
    
    if (completedCount === 0) {
      toast.info(language === 'es' ? 'No hay jobs completados para eliminar' : 'No completed jobs to delete');
      return;
    }
    
    if (!window.confirm(
      language === 'es' 
        ? `¿Eliminar ${completedCount} jobs completados?` 
        : `Delete ${completedCount} completed jobs?`
    )) {
      return;
    }
    
    try {
      await axios.delete(`${API}/seo-prospector/jobs/completed`);
      toast.success(language === 'es' ? 'Jobs completados eliminados' : 'Completed jobs deleted');
      
      // Clear current job if it was completed
      if (currentJob?.status === 'completed') {
        setCurrentJob(null);
      }
      
      fetchJobs();
    } catch (error) {
      toast.error(language === 'es' ? 'Error eliminando jobs' : 'Error deleting jobs');
    }
  };

  // ========================
  // CAMPAIGN FUNCTIONS
  // ========================

  const fetchCampaigns = async () => {
    setCampaignsLoading(true);
    try {
      const response = await axios.get(`${API}/seo-prospector/campaigns`);
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setCampaignsLoading(false);
    }
  };

  // Fetch replies for a specific campaign
  const fetchCampaignReplies = async (campaignId) => {
    setRepliesLoading(true);
    setSelectedCampaignForReplies(campaignId);
    
    try {
      let response;
      if (campaignId === '__all__') {
        response = await axios.get(`${API}/seo-prospector/all-replies`);
      } else {
        response = await axios.get(`${API}/seo-prospector/campaigns/${campaignId}/replies`);
      }
      setCampaignReplies(response.data.replies || []);
      setRepliesStats({
        totalReplies: response.data.total_replies,
        uniqueRepliers: response.data.unique_repliers,
        totalSent: response.data.total_sent,
        replyRate: response.data.reply_rate,
        campaignsSummary: response.data.campaigns_summary || null
      });
    } catch (error) {
      console.error('Error fetching replies:', error);
      toast.error(language === 'es' ? 'Error cargando respuestas' : 'Error loading replies');
      setCampaignReplies([]);
      setRepliesStats(null);
    } finally {
      setRepliesLoading(false);
    }
  };

  const handleCampaignFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setCampaignFile(file);
    } else {
      toast.error(language === 'es' ? 'Solo archivos Excel' : 'Only Excel files');
    }
  };

  const handleCreateCampaign = async () => {
    if (!campaignFile || !campaignName.trim()) {
      toast.error(language === 'es' ? 'Completa todos los campos' : 'Fill all fields');
      return;
    }

    setCreatingCampaign(true);
    
    try {
      const formData = new FormData();
      formData.append('file', campaignFile);
      formData.append('name', campaignName);
      formData.append('daily_limit', dailyLimit);

      const response = await axios.post(`${API}/seo-prospector/campaigns`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(language === 'es' ? 'Campaña creada' : 'Campaign created');
      setShowCreateCampaign(false);
      setCampaignFile(null);
      setCampaignName('');
      setDailyLimit(500);
      if (campaignFileRef.current) {
        campaignFileRef.current.value = '';
      }
      fetchCampaigns();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error creando campaña' : 'Error creating campaign'));
    } finally {
      setCreatingCampaign(false);
    }
  };

  const handleViewCampaign = async (campaignId) => {
    try {
      const response = await axios.get(`${API}/seo-prospector/campaigns/${campaignId}`);
      setSelectedCampaign(response.data);
      setShowCampaignModal(true);
    } catch (error) {
      toast.error(language === 'es' ? 'Error cargando campaña' : 'Error loading campaign');
    }
  };

  const handleSendBatch = async (campaignId) => {
    if (sendingBatch[campaignId]) return;
    setSendingBatch(prev => ({ ...prev, [campaignId]: true }));
    
    try {
      const response = await axios.post(`${API}/seo-prospector/campaigns/${campaignId}/send-batch`, null, {
        params: { batch_size: dailyLimit }
      });

      toast.success(
        language === 'es' 
          ? `${response.data.sent} emails enviados` 
          : `${response.data.sent} emails sent`
      );
      
      fetchCampaigns();
      
      if (selectedCampaign?.id === campaignId) {
        handleViewCampaign(campaignId);
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error enviando emails' : 'Error sending emails'));
    } finally {
      setSendingBatch(prev => ({ ...prev, [campaignId]: false }));
    }
  };

  const handlePauseCampaign = async (campaignId) => {
    try {
      await axios.post(`${API}/seo-prospector/campaigns/${campaignId}/pause`);
      toast.success(language === 'es' ? 'Campaña pausada' : 'Campaign paused');
      fetchCampaigns();
    } catch (error) {
      toast.error(language === 'es' ? 'Error pausando' : 'Error pausing');
    }
  };

  const handleResumeCampaign = async (campaignId) => {
    try {
      await axios.post(`${API}/seo-prospector/campaigns/${campaignId}/resume`);
      toast.success(language === 'es' ? 'Campaña reanudada' : 'Campaign resumed');
      fetchCampaigns();
    } catch (error) {
      toast.error(language === 'es' ? 'Error reanudando' : 'Error resuming');
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar esta campaña?' : 'Delete this campaign?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/seo-prospector/campaigns/${campaignId}`);
      toast.success(language === 'es' ? 'Campaña eliminada' : 'Campaign deleted');
      fetchCampaigns();
      setShowCampaignModal(false);
    } catch (error) {
      toast.error(language === 'es' ? 'Error eliminando' : 'Error deleting');
    }
  };

  // ========================
  // TEMPLATES
  // ========================

  const fetchTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const response = await axios.get(`${API}/seo-prospector/templates`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const fetchWorkers = async () => {
    try {
      const response = await axios.get(`${API}/seo-prospector/workers`);
      setWorkers(response.data.workers || []);
    } catch (error) {
      console.error('Error fetching workers:', error);
    }
  };

  const fetchWorkersSummary = async () => {
    try {
      const response = await axios.get(`${API}/seo-prospector/workers/summary`);
      setWorkersSummary(response.data.summary || []);
    } catch (error) {
      console.error('Error fetching workers summary:', error);
    }
  };

  const handleAssignWorker = async (jobId, workerId) => {
    try {
      await axios.put(`${API}/seo-prospector/job/${jobId}/assign`, null, {
        params: { worker_id: workerId || '' }
      });
      fetchJobs();
      fetchWorkersSummary();
    } catch (error) {
      toast.error('Error asignando trabajador');
    }
  };

  const handleWorkerStatus = async (jobId, status) => {
    try {
      await axios.put(`${API}/seo-prospector/job/${jobId}/assign`, null, {
        params: { worker_status: status }
      });
      fetchJobs();
      fetchWorkersSummary();
    } catch (error) {
      toast.error('Error actualizando estado');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-700', label: t.pending },
      processing: { color: 'bg-blue-100 text-blue-700', label: t.running },
      running: { color: 'bg-blue-100 text-blue-700', label: t.running },
      completed: { color: 'bg-green-100 text-green-700', label: t.completed },
      paused: { color: 'bg-gray-100 text-gray-700', label: t.pause },
      error: { color: 'bg-red-100 text-red-700', label: t.error },
    };
    const badge = badges[status] || badges.pending;
    return <Badge className={badge.color}>{badge.label}</Badge>;
  };

  const getWorkerStatusBadge = (ws) => {
    if (ws === 'finished') return <Badge className="bg-green-100 text-green-700">{language === 'es' ? 'Finalizado' : 'Finished'}</Badge>;
    if (ws === 'in_progress') return <Badge className="bg-amber-100 text-amber-700">{language === 'es' ? 'En proceso' : 'In progress'}</Badge>;
    return <Badge variant="outline" className="text-slate-400">{language === 'es' ? 'Sin asignar' : 'Unassigned'}</Badge>;
  };

  const getWorkerName = (workerId) => {
    const w = workers.find(w => w.id === workerId);
    return w ? w.name : '';
  };

  return (
    <div className="space-y-6" data-testid="seo-prospector-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Rocket className="h-6 w-6 text-primary" />
            {t.title}
          </h2>
          <p className="text-muted-foreground">{t.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={exportSeoBackup}
            data-testid="seo-backup-export-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            {language === 'es' ? 'Exportar Todo' : 'Export All'}
          </Button>
          <label className="inline-flex items-center h-9 px-3 text-sm font-medium rounded-md border bg-background hover:bg-accent cursor-pointer">
            <Upload className="h-4 w-4 mr-2" />
            {language === 'es' ? 'Importar Todo' : 'Import All'}
            <input
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={(e) => { importSeoBackup(e.target.files?.[0]); e.target.value = ''; }}
              data-testid="seo-backup-import-input"
            />
          </label>
        </div>
      </div>

      <Tabs defaultValue="scraper" className="w-full">
        <TabsList>
          <TabsTrigger value="scraper" className="flex items-center gap-2">
            <Search className="h-4 w-4" />
            {t.scraperTab}
          </TabsTrigger>
          <TabsTrigger value="campaigns" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            {t.campaignsTab}
            {campaigns.length > 0 && (
              <Badge variant="secondary" className="ml-1">{campaigns.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="replies" className="flex items-center gap-2">
            <Send className="h-4 w-4 rotate-180" />
            {t.repliesTab}
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <Languages className="h-4 w-4" />
            {t.templatesTab}
          </TabsTrigger>
        </TabsList>

        {/* ======================== */}
        {/* SCRAPER TAB */}
        {/* ======================== */}
        <TabsContent value="scraper">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Upload Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5 text-primary" />
                  {t.uploadExcel}
                </CardTitle>
                <CardDescription>{t.uploadDescription}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".xlsx,.xls"
                  className="hidden"
                />
                
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/50 transition-colors"
                >
                  <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                  {uploadFile ? (
                    <div>
                      <p className="font-medium">{uploadFile.name}</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {language === 'es' ? 'Click para cambiar' : 'Click to change'}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="font-medium">{t.selectFile}</p>
                      <p className="text-sm text-muted-foreground mt-1">.xlsx, .xls</p>
                    </div>
                  )}
                </div>

                <Button 
                  onClick={handleUpload} 
                  disabled={!uploadFile || uploading}
                  className="w-full"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t.processing}
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-2" />
                      {t.startScraping}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Current Job Status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  {t.jobStatus}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {currentJob ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Status</span>
                      {getStatusBadge(currentJob.status)}
                    </div>
                    
                    {(currentJob.status === 'processing' || currentJob.status === 'pending') && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>{t.processed}</span>
                          <span>{currentJob.processed || 0} / {currentJob.total_domains}</span>
                        </div>
                        <Progress 
                          value={((currentJob.processed || 0) / currentJob.total_domains) * 100} 
                        />
                      </div>
                    )}

                    <div className="grid grid-cols-4 gap-3 text-center">
                      <div className="bg-muted/50 rounded-lg p-3">
                        <p className="text-2xl font-bold">{currentJob.total_domains}</p>
                        <p className="text-xs text-muted-foreground">{t.totalDomains}</p>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-3">
                        <p className="text-2xl font-bold">{currentJob.processed || 0}</p>
                        <p className="text-xs text-muted-foreground">{t.processed}</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3">
                        <p className="text-2xl font-bold text-green-600">{currentJob.emails_found || 0}</p>
                        <p className="text-xs text-green-600">{t.emailsFound}</p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-3">
                        <p className="text-2xl font-bold text-blue-600">{currentJob.contacts_found || 0}</p>
                        <p className="text-xs text-blue-600">{language === 'es' ? 'Formularios' : 'Forms'}</p>
                      </div>
                    </div>

                    {currentJob.status === 'completed' && (
                      <div className="space-y-2">
                        <Button 
                          onClick={() => handleDownloadResult(currentJob.id)}
                          className="w-full"
                          data-testid="download-all-btn"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          {t.downloadResults}
                        </Button>
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadFound(currentJob.id)}
                            className="text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700"
                            data-testid="download-found-btn"
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            {language === 'es' ? 'Encontrados' : 'Found'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadNotFound(currentJob.id)}
                            className="text-orange-600 border-orange-200 hover:bg-orange-50 hover:text-orange-700"
                            data-testid="download-not-found-btn"
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            {language === 'es' ? 'No encontrados' : 'Not found'}
                          </Button>
                        </div>
                      </div>
                    )}

                    {currentJob.error && (
                      <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
                        <AlertCircle className="h-4 w-4 inline mr-2" />
                        {currentJob.error}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Search className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>{language === 'es' ? 'Sube un Excel para comenzar' : 'Upload an Excel to start'}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent Jobs */}

          {/* Worker Summary */}
          {workersSummary.length > 0 && (
            <Card className="mt-6">
              <CardHeader className="py-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    {language === 'es' ? 'Equipo' : 'Team'}
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setShowWorkerManager(!showWorkerManager)} className="h-7 text-xs">
                    <Settings className="h-3.5 w-3.5 mr-1" />
                    {language === 'es' ? 'Gestionar' : 'Manage'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex flex-wrap gap-3">
                  {workersSummary.map(w => (
                    <div key={w.id} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 text-sm">
                      <span className="font-medium">{w.name}</span>
                      <Badge variant="outline" className="text-xs">{w.total}</Badge>
                      {w.finished > 0 && <Badge className="bg-green-100 text-green-700 text-xs">{w.finished}</Badge>}
                      {w.in_progress > 0 && <Badge className="bg-amber-100 text-amber-700 text-xs">{w.in_progress}</Badge>}
                    </div>
                  ))}
                </div>

                {/* Worker Manager (toggle) */}
                {showWorkerManager && (
                  <div className="mt-4 p-3 border rounded-lg bg-white space-y-3">
                    <div className="flex gap-2">
                      <Input
                        value={newWorkerName}
                        onChange={(e) => setNewWorkerName(e.target.value)}
                        placeholder={language === 'es' ? 'Nombre del trabajador...' : 'Worker name...'}
                        className="h-8 text-sm"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && newWorkerName.trim()) {
                            axios.post(`${API}/seo-prospector/workers`, null, { params: { name: newWorkerName.trim() } })
                              .then(() => { setNewWorkerName(''); fetchWorkers(); fetchWorkersSummary(); toast.success('Trabajador añadido'); })
                              .catch((err) => toast.error(err.response?.data?.detail || 'Error'));
                          }
                        }}
                      />
                      <Button size="sm" className="h-8" onClick={() => {
                        if (!newWorkerName.trim()) return;
                        axios.post(`${API}/seo-prospector/workers`, null, { params: { name: newWorkerName.trim() } })
                          .then(() => { setNewWorkerName(''); fetchWorkers(); fetchWorkersSummary(); toast.success('Trabajador añadido'); })
                          .catch((err) => toast.error(err.response?.data?.detail || 'Error'));
                      }}>
                        <Plus className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs whitespace-nowrap border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid="dedupe-workers-btn"
                        onClick={() => {
                          const msg = language === 'es'
                            ? '¿Limpiar trabajadores duplicados? Mantendrá el más antiguo de cada nombre y reasignará los jobs.'
                            : 'Clean duplicate workers? Keeps the oldest of each name and reassigns jobs.';
                          if (!window.confirm(msg)) return;
                          axios.post(`${API}/seo-prospector/workers/dedupe`)
                            .then((res) => {
                              const r = res.data || {};
                              fetchWorkers();
                              fetchWorkersSummary();
                              toast.success(
                                language === 'es'
                                  ? `Limpieza OK: ${r.removed || 0} duplicados borrados, ${r.reassigned_jobs || 0} jobs reasignados, ${r.kept || 0} únicos.`
                                  : `Cleanup OK: removed=${r.removed || 0}, reassigned=${r.reassigned_jobs || 0}, kept=${r.kept || 0}.`
                              );
                            })
                            .catch((err) => toast.error(err.response?.data?.detail || 'Error'));
                        }}
                      >
                        {language === 'es' ? 'Limpiar duplicados' : 'Dedupe'}
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {workers.map(w => (
                        <div key={w.id} className="flex items-center gap-1 bg-slate-100 rounded px-2 py-1 text-sm">
                          <span>{w.name}</span>
                          <button
                            onClick={() => {
                              if (window.confirm(`¿Eliminar a ${w.name}?`)) {
                                axios.delete(`${API}/seo-prospector/workers/${w.id}`)
                                  .then(() => { fetchWorkers(); fetchWorkersSummary(); toast.success('Eliminado'); })
                                  .catch(() => toast.error('Error'));
                              }
                            }}
                            className="text-red-400 hover:text-red-600 ml-1"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card className="mt-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5" />
                  {t.recentJobs}
                </CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={fetchJobs}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {t.refresh}
                  </Button>
                  {jobs.filter(j => j.status === 'completed').length > 0 && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={deleteCompletedJobs}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {language === 'es' ? 'Limpiar completados' : 'Clear completed'}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {jobs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileSpreadsheet className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>{t.noJobs}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>{t.status}</TableHead>
                      <TableHead>{t.totalDomains}</TableHead>
                      <TableHead>{t.emailsFound}</TableHead>
                      <TableHead>{language === 'es' ? 'Formularios' : 'Forms'}</TableHead>
                      <TableHead>{language === 'es' ? 'Trabajador' : 'Worker'}</TableHead>
                      <TableHead>{language === 'es' ? 'Estado trabajo' : 'Work status'}</TableHead>
                      <TableHead>{t.createdAt}</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {jobs.map((job) => (
                      <TableRow 
                        key={job.id} 
                        className={`cursor-pointer hover:bg-muted/50 ${currentJob?.id === job.id ? 'bg-primary/10' : ''}`}
                        onClick={() => selectJob(job)}
                      >
                        <TableCell className="font-mono text-sm">{job.id}</TableCell>
                        <TableCell>{getStatusBadge(job.status)}</TableCell>
                        <TableCell>{job.total_domains}</TableCell>
                        <TableCell>
                          <span className="text-green-600 font-medium">{job.emails_found}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-blue-600 font-medium">{job.contacts_found || 0}</span>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Select
                            value={job.assigned_worker || '__none__'}
                            onValueChange={(v) => handleAssignWorker(job.id, v === '__none__' ? '' : v)}
                          >
                            <SelectTrigger className="h-7 text-xs w-28">
                              <SelectValue placeholder="-" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">-</SelectItem>
                              {workers.map(w => (
                                <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {job.assigned_worker ? (
                            <Select
                              value={job.worker_status || 'in_progress'}
                              onValueChange={(v) => handleWorkerStatus(job.id, v)}
                            >
                              <SelectTrigger className="h-7 text-xs w-28 border-0 p-0">
                                {getWorkerStatusBadge(job.worker_status || 'in_progress')}
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="in_progress">{language === 'es' ? 'En proceso' : 'In progress'}</SelectItem>
                                <SelectItem value="finished">{language === 'es' ? 'Finalizado' : 'Finished'}</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            getWorkerStatusBadge('unassigned')
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(job.created_at).toLocaleString('es-ES')}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                selectJob(job);
                              }}
                              title={language === 'es' ? 'Ver estado' : 'View status'}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {/* Resume button for interrupted/error jobs */}
                            {(job.status === 'error' || (job.status === 'processing' && job.processed < job.total_domains)) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleResumeJob(job.id);
                                }}
                                title={language === 'es' ? 'Reanudar' : 'Resume'}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              >
                                <Play className="h-4 w-4" />
                              </Button>
                            )}
                            {/* Download partial for processing jobs */}
                            {(job.status === 'processing' || job.status === 'error') && job.processed > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadPartial(job.id);
                                }}
                                title={language === 'es' ? 'Descargar Parcial' : 'Download Partial'}
                                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            )}
                            {job.status === 'completed' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadResult(job.id);
                                  }}
                                  title={language === 'es' ? 'Descargar Todo' : 'Download All'}
                                  data-testid={`download-all-${job.id}`}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadFound(job.id);
                                  }}
                                  title={language === 'es' ? 'Descargar Encontrados' : 'Download Found'}
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  data-testid={`download-found-${job.id}`}
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadNotFound(job.id);
                                  }}
                                  title={language === 'es' ? 'Descargar No Encontrados' : 'Download Not Found'}
                                  className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                  data-testid={`download-not-found-${job.id}`}
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteJob(job.id);
                              }}
                              title={language === 'es' ? 'Eliminar' : 'Delete'}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ======================== */}
        {/* CAMPAIGNS TAB */}
        {/* ======================== */}
        <TabsContent value="campaigns">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  {t.campaigns}
                </CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={fetchCampaigns}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${campaignsLoading ? 'animate-spin' : ''}`} />
                    {t.refresh}
                  </Button>
                  <Button size="sm" onClick={() => setShowCreateCampaign(true)}>
                    <Mail className="h-4 w-4 mr-2" />
                    {t.createCampaign}
                  </Button>
                </div>
              </div>
              <CardDescription>
                {language === 'es' 
                  ? 'Gestiona campañas de envío de emails gradual (20/día)' 
                  : 'Manage gradual email sending campaigns (20/day)'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {campaignsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : campaigns.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Mail className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>{t.noCampaigns}</p>
                  <Button className="mt-4" onClick={() => setShowCreateCampaign(true)}>
                    <Mail className="h-4 w-4 mr-2" />
                    {t.createCampaign}
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{language === 'es' ? 'Nombre' : 'Name'}</TableHead>
                      <TableHead>{t.status}</TableHead>
                      <TableHead>{t.totalEmails}</TableHead>
                      <TableHead>{t.sent}</TableHead>
                      <TableHead>{t.lastSent}</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {campaigns.map((campaign) => (
                      <TableRow key={campaign.id}>
                        <TableCell className="font-medium">{campaign.name}</TableCell>
                        <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                        <TableCell>{campaign.total_emails}</TableCell>
                        <TableCell>
                          <span className="text-green-600 font-medium">{campaign.sent_count}</span>
                          <span className="text-muted-foreground"> / {campaign.total_emails}</span>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {campaign.last_sent_at 
                            ? new Date(campaign.last_sent_at).toLocaleString('es-ES')
                            : '-'
                          }
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewCampaign(campaign.id)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSendBatch(campaign.id)}
                              disabled={sendingBatch[campaign.id] || campaign.sent_count >= campaign.total_emails}
                            >
                              {sendingBatch[campaign.id] ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Send className="h-4 w-4" />
                              )}
                            </Button>
                            {campaign.status === 'paused' ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResumeCampaign(campaign.id)}
                              >
                                <Play className="h-4 w-4 text-green-600" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePauseCampaign(campaign.id)}
                              >
                                <Pause className="h-4 w-4 text-yellow-600" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteCampaign(campaign.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ======================== */}
        {/* TEMPLATES TAB */}
        {/* ======================== */}
        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Languages className="h-5 w-5 text-primary" />
                    {t.templates}
                  </CardTitle>
                  <CardDescription>
                    {language === 'es' ? 'Gestiona las plantillas de email para tus campañas' : 'Manage email templates for your campaigns'}
                  </CardDescription>
                </div>
                <Button onClick={() => {
                  setEditingTemplate({ name: '', language: 'ES', subject: '', body: '' });
                  setEditingTemplateId(null);
                }}>
                  <Plus className="h-4 w-4 mr-2" />
                  {language === 'es' ? 'Nueva plantilla' : 'New template'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Edit/Create form */}
              {editingTemplate && (
                <Card className="border-2 border-primary/30 bg-primary/5">
                  <CardContent className="pt-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>{language === 'es' ? 'Nombre' : 'Name'}</Label>
                        <Input
                          value={editingTemplate.name}
                          onChange={(e) => setEditingTemplate(prev => ({ ...prev, name: e.target.value }))}
                          placeholder={language === 'es' ? 'Ej: Propuesta enlaces FR' : 'E.g.: Link proposal FR'}
                        />
                      </div>
                      <div>
                        <Label>{language === 'es' ? 'Idioma' : 'Language'}</Label>
                        <Select value={editingTemplate.language} onValueChange={(v) => setEditingTemplate(prev => ({ ...prev, language: v }))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['ES','EN','FR','DE','IT','PT','NL','PL'].map(l => (
                              <SelectItem key={l} value={l}>{l}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>{t.subject} <span className="text-xs text-muted-foreground">({'{nombre_medio}'} = dominio)</span></Label>
                      <Input
                        value={editingTemplate.subject}
                        onChange={(e) => setEditingTemplate(prev => ({ ...prev, subject: e.target.value }))}
                        placeholder={language === 'es' ? 'Asunto del email...' : 'Email subject...'}
                      />
                    </div>
                    <div>
                      <Label>{t.body} <span className="text-xs text-muted-foreground">({'{nombre_medio}'}, {'{dominio}'} = variables)</span></Label>
                      <Textarea
                        value={editingTemplate.body}
                        onChange={(e) => setEditingTemplate(prev => ({ ...prev, body: e.target.value }))}
                        rows={8}
                        placeholder={language === 'es' ? 'Cuerpo del email...' : 'Email body...'}
                        className="text-sm font-mono"
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => { setEditingTemplate(null); setEditingTemplateId(null); }}>
                        {language === 'es' ? 'Cancelar' : 'Cancel'}
                      </Button>
                      <Button onClick={async () => {
                        if (!editingTemplate.name || !editingTemplate.subject || !editingTemplate.body) {
                          toast.error(language === 'es' ? 'Completa todos los campos' : 'Fill all fields');
                          return;
                        }
                        try {
                          if (editingTemplateId) {
                            await axios.put(`${API}/seo-prospector/templates/${editingTemplateId}`, null, {
                              params: editingTemplate
                            });
                            toast.success(language === 'es' ? 'Plantilla actualizada' : 'Template updated');
                          } else {
                            await axios.post(`${API}/seo-prospector/templates`, null, {
                              params: editingTemplate
                            });
                            toast.success(language === 'es' ? 'Plantilla creada' : 'Template created');
                          }
                          setEditingTemplate(null);
                          setEditingTemplateId(null);
                          fetchTemplates();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Error');
                        }
                      }}>
                        {editingTemplateId 
                          ? (language === 'es' ? 'Guardar cambios' : 'Save changes')
                          : (language === 'es' ? 'Crear plantilla' : 'Create template')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Templates list */}
              {templatesLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Custom templates */}
                  {templates.filter(t => !t.is_default).length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-muted-foreground mb-3">
                        {language === 'es' ? 'Mis plantillas' : 'My templates'}
                      </h3>
                      <div className="grid gap-3 md:grid-cols-2">
                        {templates.filter(t => !t.is_default).map((template) => (
                          <Card key={template.id} className="border-l-4 border-l-green-500">
                            <CardContent className="pt-4">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Badge>{template.language}</Badge>
                                  <span className="text-sm font-medium">{template.name}</span>
                                </div>
                                <div className="flex gap-1">
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => {
                                    setEditingTemplate({ name: template.name, language: template.language, subject: template.subject, body: template.body });
                                    setEditingTemplateId(template.id);
                                  }}>
                                    <Edit3 className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={async () => {
                                    if (window.confirm(language === 'es' ? '¿Eliminar esta plantilla?' : 'Delete this template?')) {
                                      try {
                                        await axios.delete(`${API}/seo-prospector/templates/${template.id}`);
                                        toast.success(language === 'es' ? 'Plantilla eliminada' : 'Template deleted');
                                        fetchTemplates();
                                      } catch { toast.error('Error'); }
                                    }
                                  }}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </div>
                              <div className="space-y-1 text-sm">
                                <p className="font-mono text-xs bg-muted p-2 rounded truncate">{template.subject}</p>
                                <p className="text-xs bg-muted p-2 rounded line-clamp-3 whitespace-pre-wrap">{template.body.substring(0, 200)}{template.body.length > 200 ? '...' : ''}</p>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Default templates */}
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground mb-3">
                      {language === 'es' ? 'Plantillas por defecto' : 'Default templates'}
                    </h3>
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                      {templates.filter(t => t.is_default).map((template) => (
                        <Card key={template.id} className="border-l-4 border-l-slate-300">
                          <CardContent className="pt-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">{template.language}</Badge>
                                <span className="text-sm font-medium">{template.name}</span>
                              </div>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => {
                                setEditingTemplate({ name: template.name, language: template.language, subject: template.subject, body: template.body });
                                setEditingTemplateId(template.id);
                              }}>
                                <Edit3 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                            <div className="space-y-1 text-sm">
                              <p className="font-mono text-xs bg-muted p-2 rounded truncate">{template.subject}</p>
                              <p className="text-xs bg-muted p-2 rounded line-clamp-3">{template.body.substring(0, 150)}...</p>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ======================== */}
        {/* REPLIES TAB */}
        {/* ======================== */}
        <TabsContent value="replies">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5 rotate-180" />
                {t.campaignReplies}
              </CardTitle>
              <CardDescription>
                {language === 'es' 
                  ? 'Ve las respuestas que has recibido de tus campañas de prospección' 
                  : 'View responses you have received from your prospecting campaigns'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Campaign Selector */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Label className="mb-2 block">{language === 'es' ? 'Seleccionar Campaña' : 'Select Campaign'}</Label>
                  <Select 
                    value={selectedCampaignForReplies || ''} 
                    onValueChange={(val) => fetchCampaignReplies(val)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t.selectCampaignToViewReplies} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">
                        {language === 'es' ? 'Todas las campañas' : 'All campaigns'}
                      </SelectItem>
                      {campaigns.map((camp) => (
                        <SelectItem key={camp.id} value={camp.id}>
                          {camp.name} ({camp.sent_count}/{camp.total_emails} {t.sent.toLowerCase()})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedCampaignForReplies && (
                  <Button
                    variant="outline"
                    onClick={() => fetchCampaignReplies(selectedCampaignForReplies)}
                    disabled={repliesLoading}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${repliesLoading ? 'animate-spin' : ''}`} />
                    {t.refresh}
                  </Button>
                )}
              </div>

              {/* Stats */}
              {repliesStats && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                      <CardContent className="p-4 text-center">
                        <p className="text-2xl font-bold text-green-700">{repliesStats.totalReplies}</p>
                        <p className="text-sm text-green-600">{language === 'es' ? 'Respuestas' : 'Replies'}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                      <CardContent className="p-4 text-center">
                        <p className="text-2xl font-bold text-blue-700">{repliesStats.uniqueRepliers}</p>
                        <p className="text-sm text-blue-600">{t.uniqueRepliers}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
                      <CardContent className="p-4 text-center">
                        <p className="text-2xl font-bold text-purple-700">{repliesStats.totalSent}</p>
                        <p className="text-sm text-purple-600">{t.totalSent}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                      <CardContent className="p-4 text-center">
                        <p className="text-2xl font-bold text-amber-700">{repliesStats.replyRate}</p>
                        <p className="text-sm text-amber-600">{t.replyRate}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Per-campaign breakdown when viewing all */}
                  {selectedCampaignForReplies === '__all__' && repliesStats.campaignsSummary && Object.keys(repliesStats.campaignsSummary).length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(repliesStats.campaignsSummary).map(([name, count]) => (
                        <Badge key={name} variant="outline" className="text-sm py-1 px-3">
                          <Mail className="h-3 w-3 mr-1.5" />
                          {name}: <span className="font-bold ml-1">{count}</span>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Replies List */}
              {repliesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : selectedCampaignForReplies ? (
                campaignReplies.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t.noReplies}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {campaignReplies.map((reply, idx) => (
                      <Card key={reply.id || idx} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold truncate">{reply.from_name || reply.from_email}</span>
                                {reply.campaign_name && selectedCampaignForReplies === '__all__' && (
                                  <Badge className="bg-primary/10 text-primary text-xs border-primary/20">
                                    <Mail className="h-3 w-3 mr-1" />
                                    {reply.campaign_name}
                                  </Badge>
                                )}
                                {reply.domain && (
                                  <Badge variant="outline" className="text-xs">
                                    <Globe className="h-3 w-3 mr-1" />
                                    {reply.domain}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground truncate mb-2">
                                {reply.from_email}
                              </p>
                              <p className="font-medium text-sm mb-2">{reply.subject}</p>
                              <p className="text-sm text-slate-600 line-clamp-2">
                                {reply.body_preview}
                              </p>
                            </div>
                            <div className="text-right text-sm text-muted-foreground whitespace-nowrap">
                              {reply.date && new Date(reply.date).toLocaleDateString('es-ES', {
                                day: '2-digit',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t.selectCampaignToViewReplies}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Campaign Dialog */}
      <Dialog open={showCreateCampaign} onOpenChange={setShowCreateCampaign}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.createCampaign}</DialogTitle>
            <DialogDescription>
              {language === 'es' 
                ? 'Sube el Excel generado por el buscador de emails' 
                : 'Upload the Excel generated by the email finder'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t.campaignName}</Label>
              <Input
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder={language === 'es' ? 'Ej: Campaña Enero 2025' : 'E.g.: January 2025 Campaign'}
              />
            </div>

            <div className="space-y-2">
              <Label>{t.uploadEmailsFile}</Label>
              <input
                type="file"
                ref={campaignFileRef}
                onChange={handleCampaignFileSelect}
                accept=".xlsx,.xls"
                className="hidden"
              />
              <div 
                onClick={() => campaignFileRef.current?.click()}
                className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary/50"
              >
                {campaignFile ? (
                  <p className="font-medium">{campaignFile.name}</p>
                ) : (
                  <p className="text-muted-foreground">{t.selectFile}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t.dailyLimit}</Label>
              <Select value={String(dailyLimit)} onValueChange={(v) => setDailyLimit(Number(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 emails/día</SelectItem>
                  <SelectItem value="50">50 emails/día</SelectItem>
                  <SelectItem value="100">100 emails/día</SelectItem>
                  <SelectItem value="200">200 emails/día</SelectItem>
                  <SelectItem value="500">500 emails/día</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateCampaign(false)}>
              {t.cancel}
            </Button>
            <Button onClick={handleCreateCampaign} disabled={creatingCampaign}>
              {creatingCampaign ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Mail className="h-4 w-4 mr-2" />
              )}
              {t.create}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Detail Modal */}
      <Dialog open={showCampaignModal} onOpenChange={setShowCampaignModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{t.viewCampaign}: {selectedCampaign?.name}</DialogTitle>
          </DialogHeader>
          
          {selectedCampaign && (
            <div className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold">{selectedCampaign.total_emails}</p>
                  <p className="text-xs text-muted-foreground">{t.totalEmails}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-green-600">
                    {selectedCampaign.recipients_summary?.sent || 0}
                  </p>
                  <p className="text-xs text-green-600">{t.sent}</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-yellow-600">
                    {selectedCampaign.recipients_summary?.pending || 0}
                  </p>
                  <p className="text-xs text-yellow-600">{t.pendingEmails}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-red-600">
                    {selectedCampaign.recipients_summary?.failed || 0}
                  </p>
                  <p className="text-xs text-red-600">{t.failedEmails}</p>
                </div>
              </div>

              {/* Send Button */}
              <Button 
                onClick={() => handleSendBatch(selectedCampaign.id)}
                disabled={sendingBatch[selectedCampaign.id] || (selectedCampaign.recipients_summary?.pending || 0) === 0}
                className="w-full"
              >
                {sendingBatch[selectedCampaign.id] ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                {t.sendBatch} ({dailyLimit} emails)
              </Button>

              {/* Recipients Table */}
              <div>
                <h4 className="font-medium mb-2">{t.recipients}</h4>
                <div className="max-h-60 overflow-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t.domain}</TableHead>
                        <TableHead>{t.email}</TableHead>
                        <TableHead>{language === 'es' ? 'Idioma' : 'Language'}</TableHead>
                        <TableHead>{t.status}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedCampaign.recipients?.map((recipient, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-sm">{recipient.domain}</TableCell>
                          <TableCell className="text-sm font-mono">{recipient.email}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{recipient.language}</Badge>
                          </TableCell>
                          <TableCell>
                            {recipient.status === 'sent' && (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            )}
                            {recipient.status === 'pending' && (
                              <Clock className="h-4 w-4 text-yellow-600" />
                            )}
                            {recipient.status === 'failed' && (
                              <XCircle className="h-4 w-4 text-red-600" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCampaignModal(false)}>
              {language === 'es' ? 'Cerrar' : 'Close'}
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDeleteCampaign(selectedCampaign?.id)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t.delete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SEOProspectorTab;
